import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import { omit } from 'lodash';
import jwtDecode from 'jwt-decode';

import { STORAGE_KEY } from '@auth_module/constants/storageKeys';
import { getErrorTransKey } from '@auth_module/constants/errors';
import { trimTrailingSlash } from '@utils/apiUtils';
import { refreshToken } from '@auth_module/services';

export interface IRequestError extends Error {
  message: string;
  config: AxiosRequestConfig;
  response: object;
  status: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: { data?: any; message: string[] };
  code?: string | number;
  error_trans_key?: string;
}

export interface GraphqlAxiosResponse extends AxiosResponse {
  data: {
    data?: any;
    errors?: {
      extensions: {
        code: string;
        response: {
          status: number;
          url: string;
          statusText: string;
          body: object;
        };
      };
      message: string;
    }[];
  };
}

/**
 * Axios instance for public endpoints.
 */
export const publicApi = axios.create({
  baseURL: `${trimTrailingSlash(import.meta.env.VITE_SERVICE_BASE_URL)}/api`,
  headers: {
    'Content-type': 'application/json',
  },
});

export const rssUploaderApi = axios.create({
  baseURL: `${trimTrailingSlash(import.meta.env.VITE_UPLOADER_API_BASE_URL)}/v1`,
  headers: {
    'x-api-key': import.meta.env.VITE_UPLOADER_API_KEY,
  },
});

/**
 * Axios instance for private endpoints.
 */
export const privateApi = axios.create({
  timeout: 120000, // 2 minutes
  baseURL: `${trimTrailingSlash(import.meta.env.VITE_SERVICE_BASE_URL)}/api`,
  headers: {
    'Content-type': 'application/json',
  },
});

/**
 * Axios instance for private endpoints with refresh token.
 * In most cases we are using react query for data fetching and refreshing is done by invalidating the query.
 * This instance is used for cases where we didn't use react query.
 */
export const privateRefreshableApi = axios.create({
  timeout: 120000, // 2 minutes
  baseURL: `${trimTrailingSlash(import.meta.env.VITE_SERVICE_BASE_URL)}/api`,
  headers: {
    'Content-type': 'application/json',
  },
});

/**
 * Axios instance for private endpoints that are sending files with refresh token.
 * In most cases we are using react query for data fetching and refreshing is done by invalidating the query.
 * This instance is used for cases where we didn't use react query.
 */
export const privateMultipartFormDataRefreshableApi = axios.create({
  timeout: 120000, // 2 minutes
  baseURL: `${trimTrailingSlash(import.meta.env.VITE_SERVICE_BASE_URL)}/api`,
});

export const adcApi = {
  POST: axios.create({
    method: 'post',
    baseURL: `https://${trimTrailingSlash(import.meta.env.VITE_ADC_API_BASE_URL)}`,
  }),
  GET: axios.create({
    method: 'get',
    baseURL: `https://${trimTrailingSlash(import.meta.env.VITE_ADC_API_BASE_URL)}`,
    headers: {
      'Content-type': 'application/json',
    },
  }),
};

/**
 * Axios instance for adc endpoints with refresh token.
 * In most cases we are using react query for data fetching and refreshing is done by invalidating the query.
 * This instance is used for cases where we didn't use react query.
 */

export const adcRefreshableApi = {
  POST: axios.create({
    method: 'post',
    baseURL: `https://${trimTrailingSlash(import.meta.env.VITE_ADC_API_BASE_URL)}`,
  }),
  GET: axios.create({
    method: 'get',
    baseURL: `https://${trimTrailingSlash(import.meta.env.VITE_ADC_API_BASE_URL)}`,
    headers: {
      'Content-type': 'application/json',
    },
  }),
};

export const queryIntegrationsApi = axios.create({
  baseURL: `${trimTrailingSlash(import.meta.env.VITE_QUERY_MANAGEMENT_API_BASE_URL)}`,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

/**
 * Axios instance for integration endpoints with refresh token.
 * In most cases we are using react query for data fetching and refreshing is done by invalidating the query.
 * This instance is used for cases where we didn't use react query.
 */

export const queryIntegrationsRefreshableApi = axios.create({
  baseURL: `${trimTrailingSlash(import.meta.env.VITE_QUERY_MANAGEMENT_API_BASE_URL)}`,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

export const graphqlApi = axios.create({
  timeout: 120000, // 2 minutes
  baseURL: `${trimTrailingSlash(import.meta.env.VITE_SOLUTIONS_EC2_GRAPHQL_URL)}/graphql`,
  headers: {
    'Content-type': 'application/json',
    Accept: 'application/json',
  },
});

// we need to set the content type for POST requests to override the default content type that axios sets (application/x-www-form-urlencoded)
adcApi.POST.defaults.headers.post['Content-Type'] = 'application/json';
adcRefreshableApi.POST.defaults.headers.post['Content-Type'] = 'application/json';
privateMultipartFormDataRefreshableApi.defaults.headers.post['Content-Type'] =
  'multipart/form-data';

/**
 * Extract a meaningful message from the response
 */
function getErrorObject(error: AxiosError<IRequestError>) {
  if (!error.response) {
    return {
      ...error,
      config: error.config,
      message: error.message,
      error_trans_key: getErrorTransKey('error'),
      status: error.status,
    };
  }

  const key = error?.response?.data?.data ? Object.keys(error?.response?.data?.data)[0] : 'error';
  const value = error?.response?.data?.data?.message
    ? error?.response?.data?.data?.message
      ? Object.values(error?.response?.data?.data)[0]
      : Object.values(error?.response?.data?.data)[0]
    : error?.response?.data.message;

  return {
    ...error.response.data,
    status: error.response.status,
    message: Array.isArray(value) ? value[0] : value,
    error_trans_key: getErrorTransKey(key),
  };
}

/**
 * Attaches the provided token as an axios Authorization header.
 *
 * @param {string} token
 */
export function attachAuthorizationHeader(token: string) {
  publicApi.defaults.headers.common['Authorization'] = `Bearer ${token}`.trim();
  privateApi.defaults.headers.common['Authorization'] = `Bearer ${token}`.trim();
  privateRefreshableApi.defaults.headers.common['Authorization'] = `Bearer ${token}`.trim();
  privateMultipartFormDataRefreshableApi.defaults.headers.common['Authorization'] =
    `Bearer ${token}`.trim();
  adcApi.POST.defaults.headers.common['Authorization'] = `Bearer ${token}`.trim();
  adcApi.GET.defaults.headers.common['Authorization'] = `Bearer ${token}`.trim();
  adcRefreshableApi.POST.defaults.headers.common['Authorization'] = `Bearer ${token}`.trim();
  adcRefreshableApi.GET.defaults.headers.common['Authorization'] = `Bearer ${token}`.trim();
  queryIntegrationsApi.defaults.headers.common['Authorization'] = `Bearer ${token}`.trim();
  queryIntegrationsRefreshableApi.defaults.headers.common['Authorization'] =
    `Bearer ${token}`.trim();
  graphqlApi.defaults.headers.common['Authorization'] = `Bearer ${token}`.trim();
}

function normalize(response: AxiosResponse) {
  let data = {};
  let response_meta = {};

  if (response.data.data) {
    data = response.data.data;
    response_meta = { message: response.data.message, success: response.data.success };
  } else if (response.data) {
    data = response.data;
    response_meta = {};
  }

  return { ...response, data, response_meta, ...omit(response.data, ['data']) };
}

function normalizeGraphql(response: GraphqlAxiosResponse) {
  let data = {};

  // because graphql sends errors in the data object we check for last error and return it as rejection
  // in this way we can handle errors in the same way as we do for other requests
  if (response.data.errors?.length) {
    const status =
      response.data.errors[response.data.errors.length - 1].extensions?.response?.status;
    // for now we are only handling this way if token is expired or too many requests or if there is a server error
    // for rest we will have missing data, and that should be handled on component level
    if ((status && [401, 429].includes(status)) || status >= 500) {
      return Promise.reject({
        error: response.data.errors[response.data.errors.length - 1],
        status: response.data.errors[response.data.errors.length - 1].extensions.response.status,
      });
    }
  }

  if (response.data.data) {
    data = response.data.data;
  } else if (response.data) {
    data = response.data;
  }

  return { ...response, data, ...omit(response.data, ['data']) };
}

function reject(error: AxiosError<IRequestError>) {
  return Promise.reject(getErrorObject(error));
}

let isRefreshing = false;
let failedRequestQueue: ((token: string) => void)[] = [];

function isTokenExpired(token: string) {
  const decoded = jwtDecode(token) as { exp: number };
  // decoded.exp is in seconds
  return decoded.exp * 1000 < Date.now();
}

async function rejectRefreshable(error: AxiosError) {
  const status = error?.response?.status;
  const config = error.config;
  if (status === 401) {
    const token = Cookies.get(STORAGE_KEY.auth_token);

    if (token && !isRefreshing && isTokenExpired(token) && config) {
      isRefreshing = true;
      try {
        const newToken = await refreshToken();
        isRefreshing = false;
        onRefreshed(newToken);
      } catch (error) {
        const axiosError = error as AxiosError;
        isRefreshing = false;
        if (isTokenExpired(token) && axiosError?.response?.status === 404) {
          Cookies.remove(STORAGE_KEY.auth_token);
          window.location.reload();
        }
      }
    }

    return new Promise((resolve) => {
      failedRequestQueue.push((token) => {
        if (token && config?.headers) {
          const retryRequest = axios.create();
          if (config.headers['Content-Type'] === 'multipart/form-data') {
            retryRequest.defaults.headers.post['Content-Type'] = 'multipart/form-data';
          } else {
            retryRequest.defaults.headers.post['Content-Type'] = 'application/json';
          }
          resolve(
            retryRequest({
              ...config,
              headers: { ...config.headers, Authorization: `Bearer ${token}` },
            })
          );
        }
      });
    });
  }
  return Promise.reject(getErrorObject(error as AxiosError<IRequestError>));
}

function onRefreshed(token: string) {
  // Timeout needed in case the `onRefreshed` function is called before all the failed requests are subscribed.
  setTimeout(() => {
    failedRequestQueue.forEach((cb) => cb(token));
    failedRequestQueue = [];
  }, 250);
}

const token = Cookies.get(STORAGE_KEY.auth_token) || '';

if (token.length) {
  attachAuthorizationHeader(token);
}

publicApi.interceptors.response.use(normalize, reject);
rssUploaderApi.interceptors.response.use(normalize, reject);
privateApi.interceptors.response.use(normalize, reject);
privateRefreshableApi.interceptors.response.use(normalize, rejectRefreshable);
privateMultipartFormDataRefreshableApi.interceptors.response.use(normalize, rejectRefreshable);
adcApi.POST.interceptors.response.use(normalize, reject);
adcApi.GET.interceptors.response.use(normalize, reject);
adcRefreshableApi.POST.interceptors.response.use(normalize, rejectRefreshable);
adcRefreshableApi.GET.interceptors.response.use(normalize, rejectRefreshable);
queryIntegrationsApi.interceptors.response.use(normalize, reject);
queryIntegrationsRefreshableApi.interceptors.response.use(normalize, rejectRefreshable);
graphqlApi.interceptors.response.use(normalizeGraphql, reject);
